import { Response } from './response';
import { Service } from './service';

export interface User {
  id?: number;
  email?: string;
  name?: string;
  company?: string;
  status?: 'invited' | 'active' | 'inactive';
  roles?: Role[];
  permissions?: Permission[];
  created_at?: number;
  updated_at?: number;
}

export interface UpdatePasswordDTO {
  id: number;
  password: string;
  new_password: string;
}

export interface RegisterDTO {
  name: string;
  email: string;
  subdomain: string;
  company: string;
  website: string;
  service: Service;
  planId: string;
}

export interface RegisterInfo {
  service: Service;
  planId: string;
  token: string;
  subdomain: string;
}

export interface RegisterResponse extends Response {
  payment_token: string;
}

export interface SubscribeDTO {
  payment_token: string;
  plan_id: string;
  stripe_token_id: string;
  subdomain: string;
}

export interface JoinDTO {
  email: string;
  password: string;
  token: string;
}

export interface ResetDTO {
  email: string;
  password: string;
  token: string;
}

export interface Role {
  id?: number;
  name: string;
  created_at?: number;
  updated_at?: number;
}

export interface Permission {
  id?: number;
  name: string;
  created_at?: number;
  updated_at?: number;
}

export interface Jwt {
  token: string;
  exp: number;
}

export interface JwtDTO {
  token: string;
  exp: number;
  user: User;
  status?: 'error' | 'success';
  message?: string;
}

export interface InviteDTO {
  email: string;
  name: string;
  roles: number[];
}

export enum Roles {
  ROLE_SERVICE_ADMIN = 'service admin', // super admi
  ROLE_MERCHANT_ADMIN = 'merchant admin'
}

export enum Permissions {
  // billing
  UPDATE_CARD = 'update card',
  CREATE_CARD = 'create card',
  // deployment
  QUERY_DEPLOYMENT_VALUES = 'query deployment values',
  QUERY_DEPLOYMENTS = 'query deployments',
  DEPLOY_DEPLOYMENT = 'deploy deployment',
  CREATE_CNAME = 'create cname',
  DELETE_CNAME = 'delete cname',
  UPDATE_DEPLOYMENT_VALUES = 'update deployment values',
  CREATE_DEPLOYMENT = 'create deployment',
  BULK_UPGRADE_DEPLOYMENTS = 'bulk upgrade deployments',
  // merchant
  QUERY_ALL_MERCHANTS = 'query all merchants',
  QUERY_MERCHANTS = 'query merchants',
  QUERY_MERCHANT = 'query merchant',
  QUERY_MERCHANT_SITE_STATS = 'query merchant site stats',
  CREATE_MERCHANT = 'create merchant',
  UPDATE_MERCHANT = 'update merchant',
  DELETE_MERCHANT = 'delete merchant',
  CREATE_MERCHANT_DOMAIN = 'create merchant domain',
  UPDATE_MERCHANT_DOMAIN = 'update merchant domain',
  DELETE_MERCHANT_DOMAIN = 'delete merchant domain',
  CREATE_AFFILIATE_LINK = 'create affiliate link',
  UPDATE_AFFILIATE_LINK = 'update affiliate link',
  DELETE_AFFILIATE_LINK = 'delete affiliate link',
  // network
  QUERY_NETWORK = 'query network',
  QUERY_NETWORKS = 'query networks',
  SEARCH_NETWORKS = 'search networks',
  QUERY_NETWORK_TEMPLATES = 'query network templates',
  QUERY_NETWORK_ACCOUNT_OPTION = 'query network account option',
  QUERY_NETWORK_ACCOUNT_OPTIONS = 'query network account options',
  CREATE_NETWORK = 'create network',
  UPDATE_NETWORK = 'update network',
  DELETE_NETWORK = 'delete network',
  CREATE_NETWORK_TEMPLATE = 'create network template',
  UPDATE_NETWORK_TEMPLATE = 'update network template',
  DELETE_NETWORK_TEMPLATE = 'delete network template',
  CREATE_NETWORK_MERCHANT = 'create network merchant',
  DELETE_NETWORK_MERCHANT = 'delete network merchant',
  CREATE_NETWORK_DOMAIN = 'create network domain',
  DELETE_NETWORK_DOMAIN = 'delete network domain',
  UPDATE_NETWORK_OPTION = 'update network option',
  // plan
  CHANGE_PLAN = 'change plan',
  // service
  CREATE_SERVICE_VERSION = 'create service version',
  // setting
  QUERY_SETTING = 'query setting',
  QUERY_SETTINGS = 'query settings',
  MUTATE_SETTING = 'mutate setting',
  CREATE_SETTING_LIST_ITEM = 'create setting list item',
  DELETE_SETTING_LIST_ITEM = 'delete setting list item',
  // subscription
  QUERY_SUBSCRIPTIONS = 'query subscriptions',
  QUERY_CURRENT_SUBSCRIPTIONS = 'query current subscription',
  QUERY_INVOICE_FILE = 'query invoice file',
  CANCEL_SUBSCRIPTION = 'cancel subscription',
  // translation
  SEARCH_TRANSLATIONS = 'search translations',
  QUERY_TRANSLATIONS = 'query translations',
  QUERY_LOCALES = 'query locales',
  CREATE_TRANSLATION = 'create translation',
  UPDATE_TRANSLATION = 'update translation',
  DELETE_TRANSLATION = 'delete translation',
  UPSERT_TRANSLATION = 'upsert translation',
  // user
  QUERY_ROLES = 'query roles',
  QUERY_USERS = 'query users',
  INVITE_USER = 'invite user',
  UPDATE_USER = 'update user',
  DELETE_USER = 'delete user'
}
